import type { Header } from "@tanstack/react-table";
import { type RefObject, useEffect, useRef } from "react";

// Syncs a custom header cell to TanStack column sizing — expands to measured content width and tracks live drag resize.
export function useTableHeaderColumnSizing<TData, TValue>(
	header: Header<TData, TValue>,
	cellRef: RefObject<HTMLDivElement | null>,
) {
	const setColumnSizingRef = useRef<(newSize: number) => void>(() => {});

	setColumnSizingRef.current = (newSize: number) => {
		const { table } = header.getContext();
		table.setColumnSizing((old) => ({ ...old, [header.id]: newSize }));
	};

	const { columnSizing, columnSizingInfo } = header.getContext().table.getState();
	const columnSize = columnSizing[header.id];

	useEffect(() => {
		if (cellRef.current && (columnSize == null || columnSize < cellRef.current.clientWidth)) {
			setColumnSizingRef.current(cellRef.current.clientWidth);
		}
	}, [cellRef, columnSize]);

	useEffect(() => {
		if (
			columnSizingInfo.isResizingColumn === header.id &&
			columnSizingInfo.startSize !== null &&
			columnSizingInfo.deltaOffset !== null
		) {
			const newSize = columnSizingInfo.startSize + columnSizingInfo.deltaOffset;
			if (newSize !== columnSize) {
				setColumnSizingRef.current(newSize);
			}
		}
	}, [
		columnSizingInfo.isResizingColumn,
		columnSizingInfo.startSize,
		columnSizingInfo.deltaOffset,
		header.id,
		columnSize,
	]);

	return columnSize;
}
